import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer
} from '@mui/material';
import {
  Search,
  Dashboard,
  People,
  PersonAdd,
  AccessTime,
  BeachAccess,
  Work,
  Settings,
  Description,
  Campaign,
  Stars,
  Assessment,
  Notifications,
  AccountCircle,
  Logout,
  BarChart,
  AdminPanelSettings,
  CalendarMonth,
  Payment,
  AutoAwesome,
  CheckCircle,
  Error as ErrorIcon,
  Close,
  Send,
  Warning
} from '@mui/icons-material';
import { useAuth } from '@/store/authStore';
import { aiCommandService, ParsedCommand, ExecuteResponse } from '@/services/aiCommandService';

interface CommandItem {
  id: string;
  title: string;
  description?: string;
  icon: React.ReactNode;
  path?: string;
  action?: () => void;
  keywords: string[];
  category: 'navigation' | 'action' | 'search';
  roles?: string[];
}

interface CommandBarProps {
  open: boolean;
  onClose: () => void;
}

type AIState = 'idle' | 'parsing' | 'confirming' | 'executing' | 'success' | 'error' | 'displaying';

const CommandBar: React.FC<CommandBarProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // AI state
  const [aiState, setAIState] = useState<AIState>('idle');
  const [parsedCommand, setParsedCommand] = useState<ParsedCommand | null>(null);
  const [aiResult, setAIResult] = useState<{ message: string; success: boolean; data?: any } | null>(null);
  const [confirmCooldown, setConfirmCooldown] = useState(false);

  // Define all command items
  const allCommands: CommandItem[] = [
    { id: 'dashboard', title: 'Dashboard', description: 'Go to main dashboard', icon: <Dashboard />, path: '/dashboard', keywords: ['home', 'main', 'dashboard'], category: 'navigation' },
    { id: 'profile', title: 'My Profile', description: 'View and edit your profile', icon: <AccountCircle />, path: '/profile', keywords: ['profile', 'account', 'settings', 'me'], category: 'navigation' },
    { id: 'employees', title: 'Employees', description: 'Manage employees', icon: <People />, path: '/employees', keywords: ['employees', 'staff', 'team', 'people'], category: 'navigation', roles: ['HR', 'Admin'] },
    { id: 'add-employee', title: 'Add Employee', description: 'Add a new employee', icon: <PersonAdd />, path: '/employees/add', keywords: ['add', 'new', 'employee', 'hire', 'onboard'], category: 'action', roles: ['HR', 'Admin'] },
    { id: 'attendance', title: 'Daily Tracker', description: 'View attendance records', icon: <AccessTime />, path: '/daily-tracker', keywords: ['attendance', 'tracker', 'time', 'punch', 'clock'], category: 'navigation', roles: ['HR', 'Admin', 'Program Manager'] },
    { id: 'leaves', title: 'Leave Management', description: 'Manage leave requests', icon: <BeachAccess />, path: '/leaves', keywords: ['leave', 'vacation', 'time off', 'pto', 'absence'], category: 'navigation', roles: ['HR', 'Admin', 'Employee'] },
    { id: 'jobs', title: 'Job Openings', description: 'Manage job postings', icon: <Work />, path: '/hr/jobs', keywords: ['jobs', 'openings', 'hiring', 'recruitment', 'positions'], category: 'navigation', roles: ['HR', 'Admin'] },
    { id: 'recognitions', title: 'Recognitions', description: 'Employee recognitions', icon: <Stars />, path: '/hr/recognitions', keywords: ['recognition', 'awards', 'fame', 'achievements'], category: 'navigation', roles: ['HR', 'Admin'] },
    { id: 'organization-feed', title: 'Organization Feed', description: 'Company announcements', icon: <Campaign />, path: '/organization-feed', keywords: ['feed', 'announcements', 'news', 'updates', 'posts'], category: 'navigation' },
    { id: 'calendar', title: 'Calendar', description: 'View calendar and holidays', icon: <CalendarMonth />, path: '/calendar', keywords: ['calendar', 'holidays', 'events', 'schedule'], category: 'navigation' },
    { id: 'manage-users', title: 'Manage Users', description: 'User administration', icon: <AdminPanelSettings />, path: '/manage-users', keywords: ['users', 'admin', 'manage', 'roles'], category: 'navigation', roles: ['Admin'] },
    { id: 'admin-settings', title: 'System Settings', description: 'Configure system settings', icon: <Settings />, path: '/admin/settings', keywords: ['settings', 'config', 'system', 'preferences'], category: 'navigation', roles: ['Admin'] },
    { id: 'admin-logs', title: 'System Logs', description: 'View system logs', icon: <Description />, path: '/admin/logs', keywords: ['logs', 'audit', 'history', 'activity'], category: 'navigation', roles: ['Admin'] },
    { id: 'my-attendance', title: 'My Attendance', description: 'View your attendance', icon: <AccessTime />, path: '/my-attendance', keywords: ['my', 'attendance', 'punch', 'time'], category: 'navigation', roles: ['Employee'] },
    { id: 'my-leaves', title: 'My Leaves', description: 'Apply for leave', icon: <BeachAccess />, path: '/my-leaves', keywords: ['my', 'leave', 'apply', 'request'], category: 'navigation', roles: ['Employee'] },
    { id: 'payslips', title: 'Payslips', description: 'View your payslips', icon: <Payment />, path: '/payslips', keywords: ['payslip', 'salary', 'pay', 'earnings'], category: 'navigation', roles: ['Employee', 'HR'] },
    { id: 'assessments', title: 'Assessments', description: 'Manage assessments', icon: <Assessment />, path: '/assessments', keywords: ['assessments', 'tests', 'evaluations'], category: 'navigation', roles: ['Program Manager', 'HR', 'Admin'] },
    { id: 'reports', title: 'Reports', description: 'View reports and analytics', icon: <BarChart />, path: '/reports', keywords: ['reports', 'analytics', 'stats', 'data'], category: 'navigation', roles: ['HR', 'Admin', 'Program Manager'] },
    { id: 'notifications', title: 'Notifications', description: 'View all notifications', icon: <Notifications />, path: '/notifications', keywords: ['notifications', 'alerts', 'messages'], category: 'action' },
    { id: 'logout', title: 'Logout', description: 'Sign out of your account', icon: <Logout />, action: () => { logout(); navigate('/login'); }, keywords: ['logout', 'sign out', 'exit'], category: 'action' }
  ];

  const availableCommands = allCommands.filter(cmd => !cmd.roles || cmd.roles.includes(user?.role || ''));

  const filteredCommands = searchQuery
    ? availableCommands.filter(cmd => {
        const query = searchQuery.toLowerCase();
        return cmd.title.toLowerCase().includes(query) || cmd.description?.toLowerCase().includes(query) || cmd.keywords.some(kw => kw.toLowerCase().includes(query));
      })
    : availableCommands;

  const isNaturalLanguage = useCallback((query: string): boolean => {
    if (query.length < 8) return false;
    const nlPatterns = [
      /^(create|make|add|new|set up|setup)\s/i,
      /^(apply|request|submit|take)\s/i,
      /^(approve|decline|reject|deny)\s/i,
      /^(search|find|look up|lookup|show|list|get|check|view|what|how|who)\s/i,
      /^(go to|open|navigate|take me)\s/i,
      /^(punch|clock)\s/i,
      /^(withdraw|cancel|remove)\s/i,
      /^(update|change|modify|edit|set)\s/i,
      /^(select)\s/i,
      /\b(leave|session|workshop|employee|attendance|holiday|balance|objective|milestone|okr|assessment|feedback)\b.*\b(for|from|to|with|titled|called|named)\b/i,
      /\b(from|to)\s+\d/i,
      /\b(assign|due date|reason|blocker)\b/i,
      /\bmy\s+(leave|attendance|balance|okr|okrs|profile|objectives|milestones|team|assessments|feed)\b/i,
      /\bwhat'?s\s+my\b/i,
      /\bwho\s+is\b/i,
      /\b(mark|complete|finish)\s.*(milestone|task|done)\b/i,
      /\b(acknowledge|announcements?|posts?|feed)\b/i,
      /\b(weekly\s+update|progress\s+update)\b/i,
      /\b(360|feedback|assessment)\b/i,
      /\b(payroll|salary|slip|payslip)\b/i,
      /\b(recognition|award|star performer|wall of fame)\b/i,
      /\b(job|opening|position|hiring|recruit)\b/i,
      /\b(program|session|workshop|checklist|task summary|activities)\b/i,
      /\b(user|user account|deactivate|team|department|okr period|okr cycle|lock period|activate period)\b/i,
      /^(assign|deactivate|lock|unlock|activate)\s/i,
    ];
    return nlPatterns.some(p => p.test(query.trim()));
  }, []);

  const showAIMode = searchQuery.length > 0 && (
    isNaturalLanguage(searchQuery) ||
    (filteredCommands.length === 0 && searchQuery.length >= 8)
  );

  const groupedCommands = {
    navigation: filteredCommands.filter(cmd => cmd.category === 'navigation'),
    action: filteredCommands.filter(cmd => cmd.category === 'action')
  };

  const flatCommands = [...groupedCommands.navigation, ...groupedCommands.action];

  // Handle AI command submission
  const handleAISubmit = useCallback(async () => {
    if (!searchQuery.trim() || aiState === 'parsing' || aiState === 'executing') return;

    setAIState('parsing');
    setParsedCommand(null);
    setAIResult(null);

    try {
      const parsed = await aiCommandService.parse(searchQuery.trim());

      if (parsed.action === 'unknown' || parsed.action === 'unauthorized') {
        setAIState('error');
        setAIResult({ message: parsed.summary, success: false });
        return;
      }

      if (parsed.action === 'navigate') {
        onClose();
        navigate(parsed.params?.page ? getNavPath(parsed.params.page) : '/dashboard');
        resetAIState();
        return;
      }

      if (parsed.missingFields && parsed.missingFields.length > 0) {
        setAIState('error');
        setAIResult({
          message: `${parsed.summary}\n\nMissing required info: ${parsed.missingFields.join(', ')}`,
          success: false
        });
        return;
      }

      // Auto-execute for 'auto' confirmation type
      if (parsed.confirmationType === 'auto') {
        setParsedCommand(parsed);
        setAIState('executing');
        try {
          const result = await aiCommandService.execute(parsed.action, parsed.params);
          if (result.data && (Array.isArray(result.data) || typeof result.data === 'object')) {
            setAIState('displaying');
            setAIResult({ message: result.message || 'Done', success: result.success, data: result.data });
          } else {
            setAIState('success');
            setAIResult({ message: result.message || 'Done', success: result.success });
          }
          if (result.navigate) {
            setTimeout(() => { onClose(); navigate(result.navigate!); resetAIState(); }, 1500);
          }
        } catch (err: any) {
          setAIState('error');
          setAIResult({ message: err.message || 'Failed to execute', success: false });
        }
        return;
      }

      // For 'confirm' and 'confirm_with_warning', show confirmation
      setParsedCommand(parsed);
      setAIState('confirming');

      if (parsed.confirmationType === 'confirm_with_warning') {
        setConfirmCooldown(true);
        setTimeout(() => setConfirmCooldown(false), 2000);
      }
    } catch (err: any) {
      setAIState('error');
      setAIResult({ message: err.message || 'Failed to process command', success: false });
    }
  }, [searchQuery, aiState, navigate, onClose]);

  const handleConfirm = useCallback(async () => {
    if (!parsedCommand || confirmCooldown) return;
    setAIState('executing');
    try {
      const result = await aiCommandService.execute(parsedCommand.action, parsedCommand.params);
      setAIState('success');
      setAIResult({ message: result.message || 'Action completed successfully', success: result.success });
      if (result.navigate) {
        setTimeout(() => { onClose(); navigate(result.navigate!); resetAIState(); }, 1500);
      }
    } catch (err: any) {
      setAIState('error');
      setAIResult({ message: err.message || 'Failed to execute action', success: false });
    }
  }, [parsedCommand, confirmCooldown, navigate, onClose]);

  const handleCancel = useCallback(() => { resetAIState(); }, []);

  const resetAIState = () => {
    setAIState('idle');
    setParsedCommand(null);
    setAIResult(null);
    setConfirmCooldown(false);
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!open) return;
    if (aiState === 'confirming') {
      if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); }
      else if (e.key === 'Escape') { e.preventDefault(); handleCancel(); }
      return;
    }
    if (aiState !== 'idle') {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (aiState === 'success' || aiState === 'error' || aiState === 'displaying') { resetAIState(); setSearchQuery(''); }
        else { onClose(); }
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setSelectedIndex(prev => Math.min(prev + 1, flatCommands.length - 1)); break;
      case 'ArrowUp': e.preventDefault(); setSelectedIndex(prev => Math.max(prev - 1, 0)); break;
      case 'Enter':
        e.preventDefault();
        if (showAIMode) handleAISubmit();
        else if (flatCommands[selectedIndex]) executeCommand(flatCommands[selectedIndex]);
        break;
      case 'Escape': e.preventDefault(); onClose(); break;
    }
  }, [open, selectedIndex, flatCommands, showAIMode, aiState, handleAISubmit, handleConfirm, handleCancel]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (open) { setSearchQuery(''); setSelectedIndex(0); resetAIState(); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [open]);

  const executeCommand = (cmd: CommandItem) => {
    if (cmd.action) cmd.action(); else if (cmd.path) navigate(cmd.path);
    onClose();
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      create_session: 'Create Session', apply_leave: 'Apply Leave', approve_leave: 'Approve Leave',
      decline_leave: 'Decline Leave', search_employees: 'Search', punch_in: 'Punch In',
      punch_out: 'Punch Out', update_daily_remarks: 'Update Remarks', get_attendance_history: 'Attendance History',
      get_attendance_summary: 'Attendance Summary', get_week_stats: 'Week Stats', get_leave_balance: 'Leave Balance',
      get_my_leave_requests: 'My Leaves', get_leave_types: 'Leave Types', withdraw_leave: 'Withdraw Leave',
      view_holidays: 'Public Holidays', view_optional_holidays: 'Optional Holidays',
      select_optional_holidays: 'Select Holidays', cancel_selected_holidays: 'Cancel Holidays',
      get_pending_leaves: 'Pending Leaves', get_all_leaves: 'All Leaves', get_upcoming_leaves: 'Upcoming Leaves',
      get_leave_balances_summary: 'Leave Balances', correct_leave_dates: 'Correct Dates',
      update_leave_quota: 'Update Quota',
      list_employees: 'Employee List', get_employee_details: 'Employee Details',
      get_employee_profile: 'Employee Profile', list_departments: 'Departments',
      get_daily_attendance: 'Daily Attendance', get_attendance_summary_hr: 'Attendance Summary (HR)',
      get_attendance_counts: 'Attendance Counts', get_absent_employees: 'Absent Employees',
      get_daily_updates_feed: 'Daily Updates', get_hr_dashboard: 'HR Dashboard',
      get_workforce_analytics: 'Workforce Analytics', get_hr_kpis: 'HR KPIs',
      get_my_okrs: 'My OKRs', create_individual_objective: 'Create Objective',
      create_milestone: 'Create Milestone', update_milestone_status: 'Update Milestone',
      add_okr_note: 'Add Note', create_weekly_update: 'Weekly Update',
      get_360_assessments: '360 Assessments', get_assessment_status: 'Assessment Status',
      get_assessment_results: 'Assessment Results', view_org_feed: 'Org Feed',
      acknowledge_post: 'Acknowledge Post', view_profile: 'My Profile',
      get_team_attendance: 'Team Attendance',
      create_employee: 'Create Employee', update_employee: 'Update Employee',
      delete_employee: 'Delete Employee', add_holiday: 'Add Holiday',
      bulk_add_holidays: 'Bulk Add Holidays', update_holiday: 'Update Holiday',
      delete_holiday: 'Delete Holiday', list_holidays_hr: 'All Holidays (HR)',
      list_public_holidays: 'Public Holidays', list_optional_holidays_hr: 'Optional Holidays (HR)',
      view_employee_holiday_selections: 'Holiday Selections',
      create_payroll_period: 'Create Payroll', list_payrolls: 'Payroll List',
      update_payroll: 'Update Payroll', create_employee_payroll: 'Add to Payroll',
      send_salary_slip_email: 'Send Salary Slip', download_salary_slip: 'Download Salary Slip',
      create_recognition: 'Give Recognition', list_recognitions: 'Recognitions',
      update_recognition: 'Update Recognition', delete_recognition: 'Delete Recognition',
      get_recognition_stats: 'Recognition Stats',
      create_job_opening: 'Post Job', list_job_openings: 'Job Openings',
      update_job_opening: 'Update Job', delete_job_opening: 'Delete Job',
      get_job_stats: 'Job Stats',
      list_programs: 'Programs List', get_program_details: 'Program Details',
      update_program: 'Update Program', update_program_status: 'Change Status',
      delete_program: 'Delete Program', mark_all_tasks_complete: 'Complete All Tasks',
      get_task_summary: 'Task Summary', get_program_stats: 'Program Stats',
      get_session_stats: 'Session Stats', get_program_progress: 'Program Progress',
      get_recent_activities: 'Recent Activities',
      add_checklist_item: 'Add Task', update_checklist_item: 'Update Task',
      delete_checklist_item: 'Delete Task', get_program_checklist: 'View Checklist',
      export_checklist: 'Export Checklist', get_all_checklist_items: 'All Tasks',
      list_assessments: 'Assessments', get_assessment_details: 'Assessment Details',
      create_assessment: 'Create Assessment', update_assessment: 'Update Assessment',
      delete_assessment: 'Delete Assessment', list_dimensions: 'Dimensions',
      create_dimension: 'Create Dimension', list_modules: 'Modules',
      create_module: 'Create Module', get_assessment_admin_stats: 'Assessment Stats',
      email_assessment_report: 'Email Report', get_assessment_eligibility: 'Eligibility',
      list_users: 'User List', create_user: 'Create User', update_user: 'Update User',
      delete_user: 'Delete User', list_teams: 'Teams', get_team_details: 'Team Details',
      create_team: 'Create Team', update_team: 'Update Team', delete_team: 'Delete Team',
      assign_team_manager: 'Assign Manager', create_okr_period: 'Create OKR Period',
      get_okr_periods: 'OKR Periods', activate_okr_period: 'Activate Period',
      lock_okr_period: 'Lock Period', delete_okr_period: 'Delete Period',
    };
    return labels[action] || action.replace(/_/g, ' ');
  };

  const getNavPath = (page: string) => {
    const map: Record<string, string> = {
      // Common (all roles)
      dashboard: '/dashboard', profile: '/profile/settings',
      'leave-portal': '/leave-portal', 'leave portal': '/leave-portal',
      'daily-log': '/daily-log', attendance: '/daily-log',
      'attendance-history': '/attendance-history',
      holidays: '/holidays', 'holiday-calendar': '/holiday-calendar-view',
      'organization-feed': '/organization-feed', feed: '/organization-feed',
      okr: '/okr', okrs: '/okr', objectives: '/okr',
      notifications: '/notifications', 'wall-of-fame': '/wall-of-fame',
      organization: '/organization', 'organization-setup': '/organization',
      // HR
      leaves: '/leaves', employees: '/employees',
      'daily-tracker': '/daily-tracker', payroll: '/payroll',
      jobs: '/hr/jobs', recognitions: '/hr/recognitions',
      'holiday-management': '/holiday-management',
      // Admin
      'manage-users': '/manage-users', users: '/manage-users',
      teams: '/organization?tab=teams',
      // PM
      sessions: '/sessions', reports: '/reports',
      'recent-activities': '/recent-activities',
      // Assessment Creator
      assessments: '/manage-assessments', 'manage-assessments': '/manage-assessments',
      'assessment-dashboard': '/assessment-dashboard',
    };
    return map[page?.toLowerCase()] || '/dashboard';
  };

  // Render data table for displaying results
  const renderDataDisplay = (data: any) => {
    if (!data) return null;

    // Array of objects -> table
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      const keys = Object.keys(data[0]).filter(k => !k.startsWith('_') && !['id', 'emp_id', 'employee_id', 'created_at', 'updated_at'].includes(k));
      const displayKeys = keys.slice(0, 5); // Max 5 columns

      return (
        <TableContainer sx={{ maxHeight: 250, mt: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {displayKeys.map(key => (
                  <TableCell key={key} sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'capitalize', py: 0.5,
                    backgroundColor: isDark ? '#1a1a2e' : '#f5f5f5' }}>
                    {key.replace(/_/g, ' ')}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.slice(0, 10).map((row: any, i: number) => (
                <TableRow key={i}>
                  {displayKeys.map(key => (
                    <TableCell key={key} sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {formatCellValue(row[key])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data.length > 10 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 0.5 }}>
              Showing 10 of {data.length} results
            </Typography>
          )}
        </TableContainer>
      );
    }

    // Single object -> key/value display
    if (typeof data === 'object' && !Array.isArray(data)) {
      const entries = Object.entries(data).filter(([k]) => !k.startsWith('_'));
      return (
        <Box sx={{ mt: 1 }}>
          {entries.slice(0, 8).map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                {key.replace(/_/g, ' ')}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {formatCellValue(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    return <Typography variant="body2" color="text.secondary">{String(data)}</Typography>;
  };

  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) {
      try { return new Date(value).toLocaleDateString(); } catch { return String(value); }
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <Dialog
      open={open}
      onClose={() => { onClose(); resetAIState(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: isDark ? '#1a1a2e' : '#ffffff', borderRadius: 2, overflow: 'hidden', maxHeight: '80vh' }
      }}
      sx={{ '& .MuiBackdrop-root': { backgroundColor: alpha('#000', 0.5), backdropFilter: 'blur(4px)' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search Input */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
          {showAIMode || aiState !== 'idle' ? (
            <AutoAwesome sx={{ color: theme.palette.secondary.main, mr: 1.5 }} />
          ) : (
            <Search sx={{ color: 'text.secondary', mr: 1.5 }} />
          )}
          <InputBase
            inputRef={inputRef}
            placeholder={aiState !== 'idle' ? 'AI processing...' : "Search or type a command... e.g. 'punch in', 'show my leave balance'"}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSelectedIndex(0); if (aiState !== 'idle') resetAIState(); }}
            disabled={aiState === 'parsing' || aiState === 'executing'}
            fullWidth
            sx={{ fontSize: '1rem', '& input': { padding: 0 } }}
          />
          {showAIMode && aiState === 'idle' && (
            <IconButton size="small" onClick={handleAISubmit} sx={{ color: theme.palette.secondary.main }}>
              <Send fontSize="small" />
            </IconButton>
          )}
          <Chip label="ESC" size="small" sx={{ height: 24, fontSize: '0.7rem',
            backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', ml: 1 }} />
        </Box>

        {/* AI Processing States */}
        {aiState !== 'idle' && (
          <Box sx={{ px: 2, py: 2 }}>
            {/* Parsing / Executing Loader */}
            {(aiState === 'parsing' || aiState === 'executing') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={20} color="secondary" />
                <Typography variant="body2" color="text.secondary">
                  {aiState === 'parsing' ? 'Understanding your command...' : 'Executing action...'}
                </Typography>
              </Box>
            )}

            {/* Confirmation */}
            {aiState === 'confirming' && parsedCommand && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <AutoAwesome sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">AI Command</Typography>
                  <Chip label={getActionLabel(parsedCommand.action)} size="small" color="secondary" variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem' }} />
                </Box>

                {/* Warning banner for confirm_with_warning */}
                {parsedCommand.confirmationType === 'confirm_with_warning' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 1.5, borderRadius: 1,
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}` }}>
                    <Warning sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: theme.palette.warning.main, fontWeight: 500 }}>
                      This action affects other users' data. Please review carefully.
                    </Typography>
                  </Box>
                )}

                <Box sx={{ p: 2, borderRadius: 1.5, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>{parsedCommand.summary}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {Object.entries(parsedCommand.params)
                      .filter(([key]) => !key.startsWith('_'))
                      .map(([key, value]) => (
                        <Chip key={key} label={`${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`}
                          size="small" variant="outlined"
                          sx={{ height: 24, fontSize: '0.7rem', borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)' }} />
                      ))}
                  </Box>
                  {parsedCommand.params._resolvedUser && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Matched user: {parsedCommand.params._resolvedUser.name} ({parsedCommand.params._resolvedUser.email})
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" variant="outlined" onClick={handleCancel} startIcon={<Close />}
                    sx={{ textTransform: 'none' }}>Cancel</Button>
                  <Button size="small" variant="contained"
                    color={parsedCommand.confirmationType === 'confirm_with_warning' ? 'warning' : 'secondary'}
                    onClick={handleConfirm} startIcon={<CheckCircle />} disabled={confirmCooldown}
                    sx={{ textTransform: 'none' }}>
                    {confirmCooldown ? 'Wait...' : 'Confirm & Execute'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Displaying data results */}
            {aiState === 'displaying' && aiResult && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{aiResult.message}</Typography>
                </Box>
                {renderDataDisplay(aiResult.data)}
                <Button size="small" onClick={() => { resetAIState(); setSearchQuery(''); inputRef.current?.focus(); }}
                  sx={{ mt: 1.5, textTransform: 'none', fontSize: '0.75rem' }}>New command</Button>
              </Box>
            )}

            {/* Success */}
            {aiState === 'success' && aiResult && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 22, mt: 0.2 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.success.main }}>Done!</Typography>
                  <Typography variant="body2" color="text.secondary">{aiResult.message}</Typography>
                </Box>
              </Box>
            )}

            {/* Error */}
            {aiState === 'error' && aiResult && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 22, mt: 0.2 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.error.main }}>
                    Couldn't complete that
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {aiResult.message}
                  </Typography>
                  <Button size="small" onClick={() => { resetAIState(); inputRef.current?.focus(); }}
                    sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}>Try again</Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Command list - only show when idle */}
        {aiState === 'idle' && (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {showAIMode && (
              <Box onClick={handleAISubmit} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
                cursor: 'pointer',
                backgroundColor: isDark ? 'rgba(156, 39, 176, 0.08)' : 'rgba(156, 39, 176, 0.04)',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                '&:hover': { backgroundColor: isDark ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.08)' }
              }}>
                <AutoAwesome sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Run as AI command</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Press Enter to let AI process: "{searchQuery.length > 50 ? searchQuery.slice(0, 50) + '...' : searchQuery}"
                  </Typography>
                </Box>
                <Chip label="Enter" size="small" sx={{ height: 22, fontSize: '0.65rem' }} />
              </Box>
            )}

            {filteredCommands.length === 0 && !showAIMode ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">No results found for "{searchQuery}"</Typography>
                {searchQuery.length >= 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Try typing a longer command for AI processing
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                {groupedCommands.navigation.length > 0 && (
                  <>
                    <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary',
                      textTransform: 'uppercase', fontWeight: 600 }}>Navigation</Typography>
                    <List disablePadding>
                      {groupedCommands.navigation.map((cmd, index) => (
                        <ListItem key={cmd.id} onClick={() => executeCommand(cmd)}
                          sx={{ cursor: 'pointer', py: 1, px: 2,
                            backgroundColor: selectedIndex === index ? (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)') : 'transparent',
                            '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' } }}>
                          <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.main }}>{cmd.icon}</ListItemIcon>
                          <ListItemText primary={cmd.title} secondary={cmd.description}
                            primaryTypographyProps={{ fontWeight: 500 }} secondaryTypographyProps={{ variant: 'caption' }} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
                {groupedCommands.action.length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary',
                      textTransform: 'uppercase', fontWeight: 600 }}>Actions</Typography>
                    <List disablePadding>
                      {groupedCommands.action.map((cmd, index) => {
                        const actualIndex = groupedCommands.navigation.length + index;
                        return (
                          <ListItem key={cmd.id} onClick={() => executeCommand(cmd)}
                            sx={{ cursor: 'pointer', py: 1, px: 2,
                              backgroundColor: selectedIndex === actualIndex ? (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)') : 'transparent',
                              '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' } }}>
                            <ListItemIcon sx={{ minWidth: 40, color: theme.palette.warning.main }}>{cmd.icon}</ListItemIcon>
                            <ListItemText primary={cmd.title} secondary={cmd.description}
                              primaryTypographyProps={{ fontWeight: 500 }} secondaryTypographyProps={{ variant: 'caption' }} />
                          </ListItem>
                        );
                      })}
                    </List>
                  </>
                )}
              </>
            )}
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          display: 'flex', alignItems: 'center', gap: 2,
          backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="↑↓" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
            <Typography variant="caption" color="text.secondary">Navigate</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="↵" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
            <Typography variant="caption" color="text.secondary">{showAIMode ? 'AI Command' : 'Select'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="ESC" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
            <Typography variant="caption" color="text.secondary">Close</Typography>
          </Box>
          {(showAIMode || aiState !== 'idle') && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AutoAwesome sx={{ fontSize: 14, color: theme.palette.secondary.main }} />
              <Typography variant="caption" color="secondary">AI Mode</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommandBar;
